import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from '@phosphor-icons/react';

const VoiceMessage = ({ audioUrl, isSender = false }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) {
      console.error('Audio element not found');
      return;
    }

    console.log('Audio URL:', audioUrl);
    console.log('Audio ready state:', audio.readyState);

    try {
      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        // Ensure audio is loaded before playing
        if (audio.readyState < 2) {
          console.log('Loading audio...');
          await new Promise((resolve, reject) => {
            const timeout = setTimeout(() => reject(new Error('Audio load timeout')), 10000);
            audio.addEventListener('canplay', () => {
              clearTimeout(timeout);
              resolve();
            }, { once: true });
            audio.addEventListener('error', (e) => {
              clearTimeout(timeout);
              console.error('Audio load error:', e, audio.error);
              reject(audio.error);
            }, { once: true });
            audio.load();
          });
        }
        console.log('Playing audio...');
        await audio.play();
        setIsPlaying(true);
        console.log('Audio playing successfully');
      }
    } catch (error) {
      console.error('Audio playback error:', error);
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        audioSrc: audio.src,
        networkState: audio.networkState,
        error: audio.error
      });
      setIsPlaying(false);
      // Silently handle error - user can try again
    }
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds) || !isFinite(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className={`flex items-center gap-2 ${isSender ? 'bg-wa-hover' : 'bg-white'} rounded-lg p-2 min-w-[200px] max-w-[280px]`}>
      {/* Play/Pause Button */}
      <button
        onClick={togglePlayPause}
        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${
          isSender ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-200 hover:bg-gray-300'
        } transition-colors`}
      >
        {isPlaying ? (
          <Pause size={20} weight="fill" className={isSender ? 'text-white' : 'text-gray-700'} />
        ) : (
          <Play size={20} weight="fill" className={isSender ? 'text-white' : 'text-gray-700'} />
        )}
      </button>

      {/* Waveform and Progress */}
      <div className="flex-1 flex flex-col gap-1">
        {/* Waveform visualization */}
        <div className="flex items-center gap-0.5 h-8">
          {[...Array(30)].map((_, i) => {
            const isPast = (i / 30) * 100 <= progress;
            const height = Math.random() * 100 + 20; // Random height for waveform effect
            return (
              <div
                key={i}
                className={`flex-1 rounded-full transition-all ${
                  isPast 
                    ? (isSender ? 'bg-green-600' : 'bg-blue-500')
                    : 'bg-gray-300'
                }`}
                style={{ 
                  height: `${height}%`,
                  minWidth: '2px',
                  maxWidth: '3px'
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Duration */}
      <div className={`flex-shrink-0 text-xs ${isSender ? 'text-gray-600' : 'text-gray-500'} font-mono`}>
        {formatTime(isPlaying ? currentTime : duration)}
      </div>

      {/* Hidden audio element */}
      <audio ref={audioRef} src={audioUrl} preload="metadata" />
    </div>
  );
};

export default VoiceMessage;
